import { useState } from 'react';
import type {
  RoutingBreakerSettingSource,
  RoutingBreakerSettingsResponse,
  UpdateRoutingBreakerSettingsRequest,
} from '@nadoutong/contracts';
import {
  useRoutingBreakerSettings,
  useUpdateRoutingBreakerSettings,
} from '../api/useRoutingBreakers.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Chip, Input, Switch } from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * Breaker threshold settings panel (epic #886 settings PR), rendered
 * inside the breakers drawer. Design goal per the epic: operators never
 * guess — every threshold shows a plain-language explanation, the current
 * effective value, and where that value comes from (default / env var /
 * customized), with per-field restore-to-default.
 *
 * Numeric edits accumulate in a local draft and save as one partial PUT
 * (absent = unchanged, empty input = clear customization). The enable
 * switch and per-field restores mutate immediately. When the env
 * kill-switch (`BREAKER_ENABLED=false`) is active a warning banner
 * explains that stored settings take no effect.
 */

const NUMERIC_FIELDS = [
  'consecutive_failures',
  'window_seconds',
  'window_min_failures',
  'window_failure_rate',
  'cooldown_base_seconds',
  'cooldown_cap_seconds',
  'entry_ttl_seconds',
  'half_open_trial_ttl_seconds',
] as const;

type NumericField = (typeof NUMERIC_FIELDS)[number];

const SOURCE_TONE: Record<RoutingBreakerSettingSource, ChipTone> = {
  custom: 'signal',
  env: 'warn',
  default: 'neutral',
};

function SourceChip({
  source,
  testId,
}: {
  readonly source: RoutingBreakerSettingSource;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <Chip tone={SOURCE_TONE[source]} lamp={false} testId={testId}>
      {t(`tenant_routing.breaker.settings.source.${source}`)}
    </Chip>
  );
}

type ParsedInput =
  | { kind: 'unchanged' }
  | { kind: 'clear' }
  | { kind: 'value'; value: number }
  | { kind: 'invalid' };

function parseNumericInput(
  field: NumericField,
  raw: string,
  storedValue: number | null,
): ParsedInput {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return storedValue === null ? { kind: 'unchanged' } : { kind: 'clear' };
  }
  if (field === 'window_failure_rate') {
    if (!/^\d+(\.\d+)?$/.test(trimmed)) return { kind: 'invalid' };
    const value = Number.parseFloat(trimmed);
    if (!Number.isFinite(value) || value <= 0 || value > 1) return { kind: 'invalid' };
    return value === storedValue ? { kind: 'unchanged' } : { kind: 'value', value };
  }
  if (!/^\d+$/.test(trimmed)) return { kind: 'invalid' };
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(value) || value < 1) return { kind: 'invalid' };
  return value === storedValue ? { kind: 'unchanged' } : { kind: 'value', value };
}

function NumericFieldRow({
  field,
  data,
  draftValue,
  error,
  saving,
  onDraftChange,
  onRestore,
}: {
  readonly field: NumericField;
  readonly data: RoutingBreakerSettingsResponse;
  readonly draftValue: string | undefined;
  readonly error: string | null;
  readonly saving: boolean;
  readonly onDraftChange: (value: string) => void;
  readonly onRestore: () => void;
}): JSX.Element {
  const t = useT();
  const stored = data.settings[field];
  const inputValue = draftValue ?? (stored === null ? '' : String(stored));
  const fieldId = `breaker-setting-${field}`;
  return (
    <div
      className="flex flex-col gap-1.5 rounded border border-line bg-raised p-3"
      data-testid={`breaker-setting-${field}`}
    >
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={fieldId} className="text-label text-paper-mid">
          {t(`tenant_routing.breaker.settings.field.${field}`)}
        </label>
        <SourceChip source={data.sources[field]} testId={`breaker-setting-source-${field}`} />
      </div>
      <p className="m-0 text-dense leading-5 text-paper-low">
        {t(`tenant_routing.breaker.settings.field.${field}_desc`)}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={fieldId}
          mono
          inputMode={field === 'window_failure_rate' ? 'decimal' : 'numeric'}
          className="max-w-[9rem]"
          placeholder={String(data.effective[field])}
          value={inputValue}
          invalid={error !== null}
          disabled={saving}
          onChange={(event) => onDraftChange(event.target.value)}
          data-testid={`breaker-setting-input-${field}`}
        />
        <span
          className="text-dense text-paper-low"
          data-testid={`breaker-setting-effective-${field}`}
        >
          {t('tenant_routing.breaker.settings.effective', {
            value: String(data.effective[field]),
          })}
        </span>
        {data.sources[field] === 'custom' ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={saving}
            onClick={onRestore}
            data-testid={`breaker-setting-restore-${field}`}
          >
            {t('tenant_routing.breaker.settings.restore')}
          </Button>
        ) : null}
      </div>
      {error !== null ? (
        <p role="alert" className="m-0 text-label text-bad">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function RoutingBreakerSettingsPanel(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const settings = useRoutingBreakerSettings();
  const updateSettings = useUpdateRoutingBreakerSettings();
  const [draft, setDraft] = useState<Partial<Record<NumericField, string>>>({});
  const [errors, setErrors] = useState<Partial<Record<NumericField, string>>>({});

  const data = settings.data;
  if (settings.isPending) {
    return (
      <p className="m-0 text-body text-paper-low" data-testid="breaker-settings-loading">
        {t('tenant_routing.breaker.settings.loading')}
      </p>
    );
  }
  if (data === undefined) {
    return (
      <p
        className="m-0 rounded bg-raised px-3 py-2 text-dense text-paper-low"
        data-testid="breaker-settings-load-failed"
      >
        {t('tenant_routing.breaker.settings.load_failed')}
      </p>
    );
  }

  const saving = updateSettings.isPending;

  function mutatePatch(patch: UpdateRoutingBreakerSettingsRequest, successMessage: string): void {
    updateSettings.mutate(patch, {
      onSuccess: () => toast.success(successMessage),
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  function handleRestore(field: NumericField): void {
    setDraft((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    const patch: Partial<Record<NumericField, null>> = {};
    patch[field] = null;
    mutatePatch(patch, t('tenant_routing.breaker.settings.toast.restored'));
  }

  function handleSave(): void {
    if (data === undefined) return;
    const patch: Partial<Record<NumericField, number | null>> = {};
    const nextErrors: Partial<Record<NumericField, string>> = {};
    for (const field of NUMERIC_FIELDS) {
      const raw = draft[field];
      if (raw === undefined) continue;
      const parsed = parseNumericInput(field, raw, data.settings[field]);
      if (parsed.kind === 'invalid') {
        nextErrors[field] = field === 'window_failure_rate'
          ? t('tenant_routing.breaker.settings.invalid_rate')
          : t('tenant_routing.breaker.settings.invalid_int');
        continue;
      }
      if (parsed.kind === 'clear') patch[field] = null;
      if (parsed.kind === 'value') patch[field] = parsed.value;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (Object.keys(patch).length === 0) {
      setDraft({});
      return;
    }
    updateSettings.mutate(patch, {
      onSuccess: () => {
        setDraft({});
        toast.success(t('tenant_routing.breaker.settings.toast.saved'));
      },
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  return (
    <div className="flex flex-col gap-3" data-testid="breaker-settings-panel">
      <p className="m-0 text-dense leading-5 text-paper-low">
        {t('tenant_routing.breaker.settings.hint')}
      </p>

      {data.env_kill_switch ? (
        <p
          className="m-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense leading-5 text-warn"
          data-testid="breaker-settings-kill-switch"
        >
          {t('tenant_routing.breaker.settings.kill_switch')}
        </p>
      ) : null}

      <div
        className="flex flex-col gap-1.5 rounded border border-line bg-raised p-3"
        data-testid="breaker-setting-enabled"
      >
        <div className="flex items-center justify-between gap-2">
          <Switch
            checked={data.effective.enabled}
            onCheckedChange={(next) => {
              mutatePatch({ enabled: next }, t('tenant_routing.breaker.settings.toast.saved'));
            }}
            disabled={saving || data.env_kill_switch}
            label={t('tenant_routing.breaker.settings.enabled_label')}
            testId="breaker-setting-enabled-switch"
          />
          <span className="inline-flex items-center gap-1.5">
            <SourceChip source={data.sources.enabled} testId="breaker-setting-source-enabled" />
            {data.sources.enabled === 'custom' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => {
                  mutatePatch({ enabled: null }, t('tenant_routing.breaker.settings.toast.restored'));
                }}
                data-testid="breaker-setting-restore-enabled"
              >
                {t('tenant_routing.breaker.settings.restore')}
              </Button>
            ) : null}
          </span>
        </div>
        <p className="m-0 text-dense leading-5 text-paper-low">
          {t('tenant_routing.breaker.settings.enabled_desc')}
        </p>
      </div>

      {NUMERIC_FIELDS.map((field) => (
        <NumericFieldRow
          key={field}
          field={field}
          data={data}
          draftValue={draft[field]}
          error={errors[field] ?? null}
          saving={saving}
          onDraftChange={(value) => {
            setDraft((prev) => ({ ...prev, [field]: value }));
          }}
          onRestore={() => handleRestore(field)}
        />
      ))}

      <div className="flex items-center justify-between gap-2">
        <p className="m-0 text-label text-paper-low">
          {t('tenant_routing.breaker.settings.save_hint')}
        </p>
        <Button
          variant="primary"
          loading={saving}
          onClick={handleSave}
          data-testid="breaker-settings-save"
        >
          {t('tenant_routing.breaker.settings.save')}
        </Button>
      </div>
    </div>
  );
}
